import { useState, useEffect, useCallback } from 'react';
import { Star, Users, CheckCircle, Clock, X, Plus } from 'lucide-react';
import { useApi } from '@/hooks/useApi';

// ─── Types ────────────────────────────────────────────────────

interface FeedbackCycle {
  id: string;
  reviewee_name: string;
  reviewee_title: string;
  reviewers: Array<{
    reviewer_id_hash: string;
    reviewer_name: string;
    role: string;
    submitted: boolean;
    submitted_at?: string;
  }>;
  dimensions: string[];
  status: 'pending' | 'in_progress' | 'completed';
  due_date: string;
  created_at: string;
}

interface CycleList {
  cycles: FeedbackCycle[];
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  title?: string;
  department_name?: string;
}

interface ReportDimension {
  dimension: string;
  avg_rating: number;
  response_count: number;
}

interface ReportData {
  reviewee_name: string;
  reviewee_title: string;
  response_count: number;
  reviewer_count: number;
  completion_rate: number;
  dimensions: ReportDimension[];
  themes: string[];
  verbatim_excerpts: string[];
  generated_at: string;
  minimum_for_report?: number;
  message?: string;
}

// ─── Helpers ─────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-500/20 text-amber-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    completed: 'bg-green-500/20 text-green-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] ?? styles['pending']}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function ProgressBar({ submitted, total }: { submitted: number; total: number }) {
  const pct = total > 0 ? (submitted / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden">
        <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">{submitted}/{total}</span>
    </div>
  );
}

// Radar chart SVG for 8 WorkDNA dimensions
function RadarChart({ dimensions }: { dimensions: ReportDimension[] }) {
  const n = dimensions.length;
  if (n === 0) return null;

  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 30;
  const maxRating = 5;

  const angles = dimensions.map((_, i) => (i / n) * 2 * Math.PI - Math.PI / 2);

  function point(i: number, r: number) {
    return {
      x: cx + r * Math.cos(angles[i]!),
      y: cy + r * Math.sin(angles[i]!),
    };
  }

  // Grid rings
  const rings = [1, 2, 3, 4, 5].map(v => {
    const r = (v / maxRating) * maxR;
    const pts = Array.from({ length: n }, (_, i) => point(i, r));
    const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + ' Z';
    return <path key={v} d={d} fill="none" stroke="#27272a" strokeWidth="1" />;
  });

  // Spokes
  const spokes = angles.map((_, i) => {
    const outer = point(i, maxR);
    return <line key={i} x1={cx} y1={cy} x2={outer.x} y2={outer.y} stroke="#27272a" strokeWidth="1" />;
  });

  // Data polygon
  const dataPts = dimensions.map((d, i) => point(i, (d.avg_rating / maxRating) * maxR));
  const dataD = dataPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + ' Z';

  // Labels
  const labels = dimensions.map((d, i) => {
    const labelR = maxR + 18;
    const p = point(i, labelR);
    const anchor = p.x < cx - 5 ? 'end' : p.x > cx + 5 ? 'start' : 'middle';
    return (
      <text key={i} x={p.x} y={p.y + 4} textAnchor={anchor} style={{ fontSize: 10, fill: '#a1a1aa' }}>
        {d.dimension.replace(/_/g, ' ')}
      </text>
    );
  });

  return (
    <svg width={size} height={size}>
      {rings}
      {spokes}
      <path d={dataD} fill="#f59e0b" fillOpacity="0.2" stroke="#f59e0b" strokeWidth="2" />
      {dataPts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill="#f59e0b" />
      ))}
      {labels}
    </svg>
  );
}

// Report modal
function ReportModal({ cycleId, onClose }: { cycleId: string; onClose: () => void }) {
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void fetch(`/api/people/peer-feedback/cycles/${cycleId}/report`)
      .then(r => r.json() as Promise<ReportData>)
      .then(setReport)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [cycleId]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-surface-1 border border-border rounded-2xl p-6 w-full max-w-2xl space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">360° Feedback Report</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="text-muted-foreground text-sm">Loading report…</div>
        ) : !report ? (
          <p className="text-red-400 text-sm">Failed to load report.</p>
        ) : report.message ? (
          <div className="text-center py-8">
            <Clock className="w-10 h-10 text-amber-500/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{report.message}</p>
            <p className="text-xs text-muted-foreground mt-1">{report.response_count} of {report.minimum_for_report} responses needed</p>
          </div>
        ) : (
          <>
            <div>
              <p className="font-semibold text-foreground">{report.reviewee_name}</p>
              <p className="text-sm text-muted-foreground">{report.reviewee_title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {report.response_count} responses · {report.completion_rate}% completion
              </p>
            </div>

            <div className="flex flex-col items-center">
              <h4 className="text-sm font-semibold text-foreground mb-3 self-start">WorkDNA Radar</h4>
              <RadarChart dimensions={report.dimensions} />
            </div>

            {/* Dimension scores */}
            <div className="space-y-2">
              {report.dimensions.map(d => (
                <div key={d.dimension} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-28 capitalize">{d.dimension.replace(/_/g, ' ')}</span>
                  <div className="flex-1 h-2 bg-surface-3 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full"
                      style={{ width: `${(d.avg_rating / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground w-8 text-right">{d.avg_rating}/5</span>
                </div>
              ))}
            </div>

            {report.themes.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Key Themes (AI-extracted)</h4>
                <div className="flex flex-wrap gap-2">
                  {report.themes.map((t, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs border border-amber-500/20">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {report.verbatim_excerpts.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Anonymized Excerpts</h4>
                <div className="space-y-2">
                  {report.verbatim_excerpts.slice(0, 4).map((excerpt, i) => (
                    <p key={i} className="text-xs text-muted-foreground bg-surface-3 rounded-lg p-3 italic">
                      &ldquo;{excerpt}&rdquo;
                    </p>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Create cycle modal
function CreateCycleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [revieweeId, setRevieweeId] = useState('');
  const [reviewerIds, setReviewerIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [includeSelf, setIncludeSelf] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void fetch('/api/employees?limit=100')
      .then(r => r.json() as Promise<{ employees: Employee[] }>)
      .then(d => setEmployees(d.employees ?? []))
      .catch(() => null);
  }, []);

  function toggleReviewer(id: string) {
    setReviewerIds(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  }

  async function create() {
    if (!revieweeId || !dueDate || reviewerIds.length === 0) return;
    setSaving(true);
    try {
      await fetch('/api/people/peer-feedback/cycles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewee_id: revieweeId, reviewer_ids: reviewerIds, due_date: dueDate, include_self: includeSelf }),
      });
      onCreated();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-surface-1 border border-border rounded-2xl p-6 w-full max-w-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Create 360° Feedback Cycle</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Reviewee (who is being reviewed)</label>
          <select
            value={revieweeId}
            onChange={e => setRevieweeId(e.target.value)}
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
          >
            <option value="">Select employee…</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.title ?? ''}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Due Date</label>
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-muted-foreground">Reviewers ({reviewerIds.length} selected)</label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={includeSelf} onChange={e => setIncludeSelf(e.target.checked)} className="rounded" />
              Include self-review
            </label>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 bg-surface-3 rounded-lg p-2">
            {employees.filter(e => e.id !== revieweeId).map(e => (
              <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-surface-2 px-2 py-1 rounded">
                <input
                  type="checkbox"
                  checked={reviewerIds.includes(e.id)}
                  onChange={() => toggleReviewer(e.id)}
                  className="rounded"
                />
                <span className="text-foreground">{e.first_name} {e.last_name}</span>
                {e.title && <span className="text-xs text-muted-foreground">— {e.title}</span>}
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button
            onClick={() => void create()}
            disabled={saving || !revieweeId || !dueDate || reviewerIds.length === 0}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50"
          >
            <CheckCircle className="w-3.5 h-3.5" /> {saving ? 'Creating…' : 'Create Cycle'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function PeerFeedbackPage() {
  const { data, loading, refetch } = useApi<CycleList>('people/peer-feedback/cycles');
  const [showCreate, setShowCreate] = useState(false);
  const [reportCycleId, setReportCycleId] = useState<string | null>(null);

  const handleCreated = useCallback(() => {
    setShowCreate(false);
    void refetch();
  }, [refetch]);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Star className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">360° Peer Feedback</h1>
            <p className="text-sm text-muted-foreground">Anonymous multi-rater feedback cycles for every employee</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm"
        >
          <Plus className="w-3.5 h-3.5" /> New Cycle
        </button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading feedback cycles…</div>
      ) : (data?.cycles ?? []).length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <Users className="w-10 h-10 text-purple-500/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No feedback cycles yet. Create one to start collecting 360° feedback.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(data?.cycles ?? []).map(cycle => {
            const submitted = cycle.reviewers.filter(r => r.submitted).length;
            const total = cycle.reviewers.length;
            return (
              <div key={cycle.id} className="glass rounded-xl p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-foreground">{cycle.reviewee_name}</span>
                      <StatusBadge status={cycle.status} />
                    </div>
                    <p className="text-xs text-muted-foreground">{cycle.reviewee_title} · Due {cycle.due_date}</p>
                  </div>
                  <button
                    onClick={() => setReportCycleId(cycle.id)}
                    className="text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded bg-amber-500/10 hover:bg-amber-500/20 shrink-0"
                  >
                    View Report
                  </button>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Completion</span>
                    <span className="text-foreground">{submitted}/{total} submitted</span>
                  </div>
                  <ProgressBar submitted={submitted} total={total} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                  {cycle.reviewers.map(reviewer => (
                    <div key={reviewer.reviewer_id_hash} className="flex items-center gap-1.5 text-xs">
                      {reviewer.submitted
                        ? <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />
                        : <Clock className="w-3.5 h-3.5 text-amber-400/50 shrink-0" />}
                      <span className="text-muted-foreground truncate">{reviewer.reviewer_name}</span>
                      <span className="text-muted-foreground/60 capitalize shrink-0">({reviewer.role})</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateCycleModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}

      {reportCycleId && (
        <ReportModal cycleId={reportCycleId} onClose={() => setReportCycleId(null)} />
      )}
    </div>
  );
}
